/* eslint-disable camelcase */
import App from 'next/app'
import { Router, withRouter } from 'next/router'
import React, { Fragment } from 'react'
import Head from 'next/head'
import PropTypes from 'prop-types'
import { Provider } from 'react-redux'
import { compose } from 'redux'
import LoginService from '@stvgroup/login-service'
import { GoogleTagManager } from '@next/third-parties/google' // eslint-disable-line import/no-unresolved
import {
  createDefaultTheme, ThemeProvider, CacheProvider,
} from '@stvgroup/stv-ui-components'
import createEmotionCache from '../utils/createEmotionCache'
import { applyIeClassName, isIE } from '../utils/browser'
import {
  setUserData, fetchLoginEnabled, fetchPsEnabled, setChannels,
} from '../actions'
import { checkConcurrentStream, clearConcurrentStream } from '../actions/concurrentStreamActions'
import withReduxStore from '../hocs/withReduxStore'
import withPlayerApi from '../hocs/withPlayerApi'
import getQaCookies from '../utils/qaCookies'
import getUserAgent from '../utils/getUserAgent'
import {
  PageHeader,
  PageFooter,
  MetaData,
  AdobeDTMTag,
  CMPTag,
  QaInfo,
  Modal,
} from '../components'
import SearchContainer from '../components/Search/SearchContainer'
import { initConvivaTracking, trackEvent } from '../utils/dtmHelpers'
import { showAllAds, refreshAds } from '../components/AdSlot/configureAds'
import globalStyles from '../globalStyles'
import styles from '../components/Homepage/styles'
import cookies from '../utils/cookies'
import getEnv, { HOST } from '../utils/env'
import getCurrentDomain from '../utils/getCurrentDomain'
import playerApiUtil from '../utils/playerApi'
import { STV_COOKIES } from '../constants'
import { googleTagManagerId } from '../stv.config'
import { isDeepLink } from '../utils/strings'

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache()

const defaultTheme = createDefaultTheme()

class STVApp extends App {
  static async getInitialProps({
    Component,
    ctx,
    playerApi,
    userPersonalisationOptIn,
  }) {
    const { STV_GROUPTOKEN, STV_VIP_OPTIN } = STV_COOKIES
    const groupToken = cookies.get(STV_GROUPTOKEN, ctx?.req)
    const vipOptIn = cookies.get(STV_VIP_OPTIN, ctx?.req)
    const isSecureProtocol = (ctx?.req?.protocol || window.location.protocol) === 'https:'
    const getHostname = req => (req?.headers.host.split(':')[0]
      || window.location.hostname)
    const getCurrentUrl = req => (req
      ? `${req.protocol}://${req.get('host')}${req.originalUrl}`
      : window.location.href)
    const hostname = getHostname(ctx?.req)
    const currentDomain = getCurrentDomain(hostname)
    const userAgent = getUserAgent(ctx?.req)
    const currentUrl = getCurrentUrl(ctx?.req)
    // Fetch and set premier sports enabled status first, before any other operations
    const playerApiInstance = playerApiUtil(ctx)
    const psEnabled = await ctx.reduxStore.dispatch(fetchPsEnabled(ctx, playerApiInstance))

    // Explicitly set psEnabled in Redux and wait for it to be set
    await ctx.reduxStore.dispatch({
      type: 'FETCH_PS_ENABLED_SUCCESS',
      psEnabled: Boolean(psEnabled),
    })

    // Now that psEnabled is set, we can proceed with getting pageProps and other operations
    const pageProps = await Component?.getInitialProps(ctx)
    const ieClass = isIE(userAgent) ? 'ie' : ''
    const programmeOrChannelGuid = await pageProps.programmeOrChannelGuid
    const qaCookies = getEnv() !== 'production' ? getQaCookies(ctx) : null

    let hasChannels = true
    try {
      // Now fetch login enabled status
      await ctx.reduxStore.dispatch(fetchLoginEnabled(playerApiInstance))

      // Then fetch channels
      const channels = await playerApiUtil(ctx)({
        path: '/channels',
        query: { groupToken },
      })

      // Store channels in Redux
      ctx.reduxStore.dispatch(setChannels(channels))

      hasChannels = Array.isArray(channels) && channels.length > 0
    } catch (error) {
      ctx.reduxStore.dispatch(setChannels([]))
      hasChannels = true
    }

    return {
      groupToken,
      vipOptIn,
      hasChannels,
      isSecureProtocol,
      qaCookies,
      isIE: isIE(userAgent),
      pageProps: {
        ...STVApp.defaultProps.pageProps,
        ...pageProps,
        currentDomain,
        hostname,
        userAgent,
        currentUrl,
        ieClass,
        programmeOrChannelGuid,
        hasChannels,
      },
      userPersonalisationOptIn,
    }
  }

  loginService = LoginService({
    environment: getEnv(),
    currentDomain: this.props.pageProps.currentDomain,
    isSecureProtocol: this.props.isSecureProtocol,
  })

  syncCookies = async ({
    currentDomain,
    groupToken,
    loginService,
    userData,
    subData,
  }) => {
    if (
      userData['custom:personalised']
      && userData['custom:personalised']
        !== cookies.get('STV_PERSONALISATION_OPTIN')
    ) {
      // eslint-disable-next-line no-param-reassign
      groupToken = null
    }

    if (userData && JSON.stringify(subData) !== cookies.get('STV_PLAYER_PLUS')) {
      // eslint-disable-next-line no-param-reassign
      groupToken = null // catch any changes to subscription
    }

    if (userData && !cookies.get('STV_VIP_OPTIN')) {
      // eslint-disable-next-line no-param-reassign
      groupToken = null
    }

    const domain = currentDomain
    const expires = 365
    const secure = false

    const cookieOpts = {
      expires,
      domain,
      secure,
    }

    const { STV_ALERT_COOKIE } = STV_COOKIES

    // remove serverside alert cookie if it's hanging around
    if (cookies.get(STV_ALERT_COOKIE, cookieOpts) === '[]') {
      cookies.remove(STV_ALERT_COOKIE, cookieOpts)
    }

    if (!groupToken && userData) {
      const { postal_code: postcode } = JSON.parse(userData.address.formatted)
      const userId = userData['cognito:username']
      const protocol = getEnv() === 'testing' ? 'http' : 'https'
      const newsletterData = await fetch(`${protocol}://data.api.${HOST}/v2/users/${userId}/newsletters`)
      const newsletterJson = await newsletterData.json()
      const vipStatus = (await newsletterJson?.success) && newsletterJson?.results.STV_OptedInEmail
      const gt = await loginService.getGroupToken({ postcode, userId })

      const userPersonalisationOptIn = userData['custom:personalised'] !== 'false'

      const {
        STV_GROUPTOKEN,
        STV_USERID,
        STV_PERSONALISATION_OPTIN,
        STV_VIP_OPTIN,
        STV_PLAYER_PLUS,
        STV_UNREGISTERED_GROUPTOKEN,
      } = STV_COOKIES

      cookies.set(STV_USERID, userId, cookieOpts)
      cookies.set(STV_GROUPTOKEN, gt, cookieOpts)
      cookies.set(
        STV_PERSONALISATION_OPTIN,
        userPersonalisationOptIn,
        cookieOpts
      )
      cookies.set(STV_PLAYER_PLUS, subData, cookieOpts)
      cookies.set(STV_VIP_OPTIN, vipStatus, cookieOpts)
      cookies.remove(STV_UNREGISTERED_GROUPTOKEN, cookieOpts)

      if (typeof window !== 'undefined') {
        // TODO: 'STVApp: Group token has changed, but page reload is not implemented.'
      }
    }
  }

  state = {
    alertData: [],
    alertError: false,
    isLoading: null,
  }

  getAlert = async () => {
    try {
      await fetch(
        `https://strapi.cms.${HOST}/api/alert-banners?&populate=deep&filters[Platforms][web]=true&sort=id:desc`
      )
        .then(response => response.json())
        .then((result) => {
          if (result && result?.data) {
            this.setState({ alertData: result?.data })
          }
        })
    } catch {
      this.setState({ alertError: true })
    }
  }

  isMobile = () => {
    try {
      if (window.screen.width <= 767) {
        document.createEvent('TouchEvent')
        return true
      }
      return false
    } catch (e) {
      return false
    }
  }

  trackDeepLink = () => {
    const { href = '', search = '' } = window?.location || {}
    const linkTypeisDeep = isDeepLink(href)
    if (!linkTypeisDeep) return
    const urlParams = new URLSearchParams(search)
    const source = urlParams.get('source')
    const type = urlParams.get('type')
    const guid = 'unknown'
    const trackingData = {
      dl_source: source || 'unknown',
      dl_type: type || 'none', // can be 'none', 'cw' or 'dl'
      campaign_id: window?.digitalData?.campaign,
      dl_episode: guid,
    }
    trackEvent('deepLink', trackingData)
  }

  async componentDidMount() {
    const isProduction = getEnv() === 'production'
    applyIeClassName()
    await initConvivaTracking(cookies.get('STV_USERID'), isProduction)
    Router.events.on('routeChangeComplete', () => {
      // required for DTM to be able to distinguish custom page_view events
      window.clientSideNavigation = true
      refreshAds()
    })
    this.trackDeepLink()
    // Remove - this initial page_view is configured in GTM and loaded below in <GoogleTagManager/>
    /* sendGoogleTagEvent('page_view', {
      page: this.props.router.asPath,
    }) */

    this.loginService
      .getUser()
      .then(async (userData) => {
        const forceSubscriptionInfoFetch = (window && window?.location?.search?.includes('newuser')) || false
        const subData = await this.loginService.getSubscriptionInfo(forceSubscriptionInfoFetch)
        const { vipOptIn } = this.props
        const combinedSubData = {
          ...userData, vipOptIn, subData,
        }
        this.setState({ isLoading: subData.currentlySubscribed })
        this.syncCookies({
          ...this.props.pageProps,
          groupToken: this.props.groupToken,
          userData,
          subData,
          loginService: this.loginService,
        })
        const isLivePage = this.props.router.pathname.startsWith('/live')
        if (!isLivePage) {
          this.props.reduxStore.dispatch(checkConcurrentStream(true, '', false, false))
        } else {
          // If not on a live page, clear the concurrent stream
          this.props.reduxStore.dispatch(clearConcurrentStream({ loading: true }))
        }

        this.props.reduxStore.dispatch(
          setUserData(combinedSubData)
        )
        if (!subData.currentlySubscribed) {
          showAllAds()
        }
      })
      .catch(showAllAds)

    this.getAlert()
  }

  render() {
    const pageTitle = this.props.pageProps.title
    const pageDescription = this.props.pageProps.description
    const {
      ieClass,
      currentUrl,
      currentDomain,
      loginEnabled,
      hostname,
      data,
      layout,
      image,
      summary,
      canonical,
      hasChannels,
    } = this.props.pageProps
    const { qaCookies } = this.props

    // Use the emotion cache from props (SSR) or client-side cache
    const emotionCache = this.props.emotionCache || clientSideEmotionCache

    return (
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={defaultTheme}>
          <Head>
            <meta name="build-timestamp" content={process.env.BUILD_TIMESTAMP} />
            <meta name="emotion-insertion-point" content="emotion-insertion-point" />
          </Head>
          <Provider store={this.props.reduxStore}>
            <Fragment>
              <MetaData
                currentUrl={currentUrl}
                title={pageTitle}
                description={pageDescription}
                image={image}
                summary={summary || ''}
                canonical={canonical || currentUrl}
              />
              <CMPTag layout={layout} />
              {qaCookies !== null && <QaInfo qaInfo={qaCookies} />}
              <div className={`outer-container ${layout} ${ieClass}`}>
                <div className={`inner-container ${layout}`}>
                  <PageHeader
                    groupToken={this.props.groupToken}
                    currentUrl={currentUrl}
                    currentDomain={currentDomain}
                    loginService={this.loginService}
                    loginEnabled={loginEnabled}
                    hostname={hostname}
                    isLoading={this.state.isLoading}
                    layout={data?.headerLayout ? data.headerLayout : layout}
                    alertData={this.state.alertData}
                    hasChannels={hasChannels}
                  />
                  <div className={`bumper ${layout}`} />
                  <div className="content">
                    <div className="main-content">
                      <this.props.Component
                        {...this.props.pageProps}
                        loginService={this.loginService}
                        currentDomain={currentDomain}
                      />
                      {layout !== 'home' && layout !== 'standAlonePage' && (
                        <PageFooter currentDomain={currentDomain} />
                      )}
                    </div>
                    <SearchContainer />
                  </div>
                </div>
              </div>
              <Modal />
              <AdobeDTMTag />
            </Fragment>
          </Provider>
          <style jsx>{globalStyles}</style>
          <style jsx>{styles}</style>
          {getEnv() === 'production' && (<GoogleTagManager gtmId={googleTagManagerId} />)}
        </ThemeProvider>
      </CacheProvider>
    )
  }
}

STVApp.propTypes = {
  pageProps: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    showWallpaper: PropTypes.bool,
    layout: PropTypes.oneOf([
      'default',
      'full-width',
      'home',
      'landing-page',
      'navlessLandingPage',
    ]),
    hasChannels: PropTypes.bool,
    currentDomain: PropTypes.string,
    hostname: PropTypes.string,
    userAgent: PropTypes.string,
    currentUrl: PropTypes.string,
    ieClass: PropTypes.string,
    programmeOrChannelGuid: PropTypes.string,
    data: PropTypes.shape({
      headerLayout: PropTypes.string,
    }),
    image: PropTypes.string,
    summary: PropTypes.string,
    canonical: PropTypes.string,
  }).isRequired,
  groupToken: PropTypes.string,
  vipOptIn: PropTypes.bool,
  isSecureProtocol: PropTypes.bool,
  qaCookies: PropTypes.shape({}),
  isIE: PropTypes.bool,
  userPersonalisationOptIn: PropTypes.bool,
  emotionCache: PropTypes.shape({}),
  reduxStore: PropTypes.shape({
    dispatch: PropTypes.func.isRequired,
  }).isRequired,
  router: PropTypes.shape({
    asPath: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  Component: PropTypes.func.isRequired,
}

STVApp.defaultProps = {
  groupToken: null,
  vipOptIn: false,
  isSecureProtocol: false,
  qaCookies: null,
  isIE: false,
  userPersonalisationOptIn: false,
  emotionCache: null,
}
const enhance = compose(withPlayerApi, withReduxStore, withRouter)

export default enhance(STVApp)
